import { LOCAL_FEATURE_FLAG_VALUES } from '@his/feature-flags';
import { createRoute, redirect } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { authenticatedRoute } from '@/authenticated-route';
import FullPageLoadingSpinner from '@/components/ui/full-page-loading-spinner';
import { IS_BONEYARD_BUILD } from '@/integrations/boneyard/get-boneyard-token';
import { getCurrentUser } from '@/integrations/idb/models/users';
import { FEATURE_FLAGS_CONFIG } from '@/lib/env';
import { getFeatureFlagsQueryOptions } from '@/services/use-query-feature-flags';
import * as auth from '@/shared/auth';
import { createDashboardRegistry } from './consts/dashboard-registry';
import { DASHBOARD_ROUTES } from './consts/routes';
import { resolveDashboardLoader } from './utils/resolve-dashboard-loader';

export const dashboardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: DASHBOARD_ROUTES.index,
  beforeLoad: async ({ context }) => {
    if (IS_BONEYARD_BUILD) {
      const registry = createDashboardRegistry(LOCAL_FEATURE_FLAG_VALUES);
      const role = new URLSearchParams(window.location.search).get(
        'boneyardRole',
      );
      const entry = registry.entries.find((e) => e.role === role);
      const load = entry?.load ?? registry.defaultLoad;

      if (load) {
        return { DashboardComponent: lazy(load) };
      }
    }

    const flags =
      FEATURE_FLAGS_CONFIG.mode === 'local'
        ? LOCAL_FEATURE_FLAG_VALUES
        : await context.queryClient.ensureQueryData(
            getFeatureFlagsQueryOptions(),
          );
    const registry = createDashboardRegistry(flags);

    const currentUser = await getCurrentUser({ nonNullable: true });
    const result = resolveDashboardLoader(currentUser.roles, registry);

    if (!result) {
      throw redirect({ to: auth.AUTH_ROUTES.unauthorized, replace: true });
    }

    const DashboardComponent = lazy(result.load);

    return { DashboardComponent };
  },
  component: () => {
    const { DashboardComponent } = dashboardRoute.useRouteContext();

    return (
      <Suspense fallback={<FullPageLoadingSpinner />}>
        <DashboardComponent />
      </Suspense>
    );
  },
});
